/**
 * useBrowserFullscreen — toggle native browser fullscreen (F11-style).
 *
 * Hides the URL bar, tab bar, bookmarks bar, and every other piece of
 * browser chrome — used on the "/{route}/fullscreen" ops-center routes
 * so a TV kiosk devotes 100% of pixels to the app.
 *
 * Native browser rules to keep in mind:
 *   - requestFullscreen() MUST be called from a user-gesture event
 *     handler (click / keypress). Calling it from a useEffect on
 *     mount is silently rejected by every browser except when the app
 *     is already fullscreen. We expose request() so the caller can
 *     wire it to a visible button.
 *   - Pressing Esc (or the browser's UI) exits fullscreen without
 *     going through our API. We listen for fullscreenchange to keep
 *     `active` in sync so the UI reflects reality.
 *   - Vendor-prefixed variants (webkitRequestFullscreen) covered so
 *     older Safari still works.
 */
import { useCallback, useEffect, useState } from "react";

function getFsElement() {
  if (typeof document === "undefined") return null;
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement ||
    null
  );
}

async function _request(el) {
  const fn =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.mozRequestFullScreen ||
    el.msRequestFullscreen;
  if (!fn) throw new Error("Fullscreen API unsupported in this browser");
  return fn.call(el);
}

async function _exit() {
  const fn =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.mozCancelFullScreen ||
    document.msExitFullscreen;
  if (!fn) return;
  return fn.call(document);
}

export function useBrowserFullscreen() {
  const supported =
    typeof document !== "undefined" &&
    !!(
      document.documentElement.requestFullscreen ||
      document.documentElement.webkitRequestFullscreen ||
      document.documentElement.mozRequestFullScreen ||
      document.documentElement.msRequestFullscreen
    );
  const [active, setActive] = useState(() => !!getFsElement());
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!supported) return undefined;
    const sync = () => setActive(!!getFsElement());
    const events = [
      "fullscreenchange",
      "webkitfullscreenchange",
      "mozfullscreenchange",
      "MSFullscreenChange",
    ];
    events.forEach((e) => document.addEventListener(e, sync));
    return () => {
      events.forEach((e) => document.removeEventListener(e, sync));
    };
  }, [supported]);

  const request = useCallback(async () => {
    if (!supported) return;
    try {
      setError(null);
      await _request(document.documentElement);
    } catch (err) {
      // Most common cause: called outside a user gesture. The browser
      // rejects silently in that case — surface the reason via `error`
      // so a caller UI can prompt the user to click the button.
      setError(err?.message || "Fullscreen request rejected");
    }
  }, [supported]);

  const exit = useCallback(async () => {
    try {
      await _exit();
    } catch {
      /* browsers throw if not fullscreen — ignore */
    }
  }, []);

  const toggle = useCallback(() => (active ? exit() : request()), [active, exit, request]);

  return { active, supported, error, request, exit, toggle };
}
